import { useMemo } from "react";
import { NetworkStatus, useQuery } from "@apollo/client";
import type { CubeQuery, Link } from "@models/ISchema";
import existingSubjectLeaguesQuery from "../../graphql/existingSubjectLeagues.query.graphql";

interface UseExistingLeagueOptionsProps {
  scenarioId: string;
  originId: string;
  courseId: string;
  excludedLeagueIds?: string[];
  searchTerm?: string;
  page?: number;
  pageSize?: number;
  enabled?: boolean;
}

type ExistingSubjectLeaguesQueryData = {
  cube?: Pick<CubeQuery, "links">;
};

export interface ExistingLeagueOption {
  label: string;
  value: string;
  groupCodes: string[];
  league: Link;
}

export const useExistingLeagueOptions = ({
  scenarioId,
  originId,
  courseId,
  excludedLeagueIds = [],
  searchTerm = "",
  page = 1,
  pageSize = 20,
  enabled = true,
}: UseExistingLeagueOptionsProps) => {
  const numericCourseId = Number(courseId);
  const shouldSkip =
    !enabled || !scenarioId || !originId || !courseId || !Number.isInteger(numericCourseId);

  const { data, loading, error, refetch, networkStatus } =
    useQuery<ExistingSubjectLeaguesQueryData>(existingSubjectLeaguesQuery, {
      variables: {
        scenarioId,
        originId,
        filter: {
          fields: { courseId: { eq: numericCourseId } },
          pagination: { page, size: pageSize, searchTerm },
        },
      },
      skip: shouldSkip,
      fetchPolicy: "no-cache",
      notifyOnNetworkStatusChange: true,
    });

  const excludedIds = useMemo(() => new Set(excludedLeagueIds), [excludedLeagueIds]);
  const leagues = useMemo(
    () => (data?.cube?.links?.items ?? []).filter(league => !excludedIds.has(league.id)),
    [data?.cube?.links?.items, excludedIds],
  );
  const options = useMemo<ExistingLeagueOption[]>(
    () =>
      leagues.map(league => ({
        label: league.course
          ? `${league.code} - ${league.course.code} - ${league.course.name}`
          : league.code,
        value: league.id,
        groupCodes: (league.groups ?? []).flatMap(group => (group?.code ? [group.code] : [])),
        league,
      })),
    [leagues],
  );

  return {
    leagues,
    options,
    pageInfo: data?.cube?.links?.pageInfo,
    isLoading: loading,
    isRetrying: networkStatus === NetworkStatus.refetch,
    error,
    isEmpty: !shouldSkip && !loading && !error && options.length === 0,
    retry: refetch,
  };
};

export default useExistingLeagueOptions;
